import { useState, useEffect } from 'react';
import { apiErrorText } from '../lib/apiError';
import { AlertCircle, CreditCard, ExternalLink, CheckCircle2, Clock, X, RefreshCw, Shield, Lock, Building, Loader2 } from 'lucide-react';
import apiV2 from '../utils/apiV2';
import { toast } from 'sonner';

const StripeConnectBanner = ({ onDismiss }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showTrustModal, setShowTrustModal] = useState(false);
  
  const orgId = localStorage.getItem('organizationId');

  useEffect(() => {
    // Check for OAuth callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const stripeSuccess = urlParams.get('stripe_success');
    const stripeError = urlParams.get('stripe_error');
    const errorMessage = urlParams.get('message');
    
    if (stripeSuccess) {
      toast.success('Stripe account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      window.dispatchEvent(new Event('setupDataChanged'));
    } else if (stripeError) {
      toast.error(`Stripe connection failed: ${errorMessage || stripeError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    checkStatus();
  }, []);

  const checkStatus = async (forceRefresh = false) => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    try {
      const response = await apiV2.get(`/organizations/${orgId}/stripe-connect/status`, {
        params: { refresh: forceRefresh }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error checking Stripe Connect status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    setShowTrustModal(true);
  };
  
  const handleConfirmStripe = async () => {
    setShowTrustModal(false);
    setConnecting(true);
    
    try {
      // Get OAuth URL from backend
      const response = await apiV2.get(`/organizations/${orgId}/stripe-connect/oauth-url`);
      const { url } = response.data;
      
      if (!url) {
        throw new Error('Failed to generate Stripe connection URL');
      }
      
      // Redirect to Stripe OAuth
      window.location.href = url;
      
    } catch (err) {
      console.error('Error starting Stripe connection:', err);
      toast.error(apiErrorText(err, 'Failed to connect to Stripe'));
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await apiV2.get(`/organizations/${orgId}/stripe-connect/dashboard`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (err) {
      // For Standard accounts, just open the Stripe dashboard directly
      window.open('https://dashboard.stripe.com', '_blank');
    }
  };

  if (loading) {
    return null;
  }

  // Don't show if already fully connected
  if (status?.onboardingComplete) {
    return null;
  }

  // Show different banners based on status
  if (!status?.connected || status?.needsReconnect) {
    // Not connected at all OR needs to reconnect (old Express account)
    return (
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-6 relative">
        {status?.needsReconnect && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>⚠️ Action Required:</strong> {status.error || 'Your Stripe account needs to be reconnected to process payments.'}
            </p>
          </div>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}
        
        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          <div className="bg-blue-600 p-2 sm:p-3 rounded-xl flex-shrink-0">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              💰 Set Up Payment Processing
            </h3>
            <p className="text-sm sm:text-base text-gray-700 mb-3">
              Connect your Stripe account to start receiving payments from customers. 
              You'll be redirected to Stripe to securely connect or create your account.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="bg-[#635BFF] hover:bg-[#5851DB] text-white px-4 sm:px-6 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base disabled:opacity-50"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                    </svg>
                    Connect with Stripe
                  </>
                )}
              </button>
              <button
                onClick={() => checkStatus(true)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <RefreshCw className="w-4 h-4" />
                Check Status
              </button>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 justify-center sm:justify-start">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Secure • Takes 2-3 min
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust Modal - Shows before Stripe OAuth redirect */}
        {showTrustModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#635BFF] to-[#5851DB] px-6 py-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Connect Your Stripe Account</h3>
                    <p className="text-blue-100 text-sm">Secure Payment Setup</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="px-6 py-5 space-y-4">
                <p className="text-gray-700">
                  You'll be redirected to <strong>Stripe</strong> to connect your existing account or create a new one.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 p-1.5 rounded-full mt-0.5">
                      <Lock className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Direct Payments to You</p>
                      <p className="text-sm text-gray-600">Customer payments go directly to your Stripe account. BookingFlows only receives the platform fee.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 p-1.5 rounded-full mt-0.5">
                      <Shield className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">You Control Your Account</p>
                      <p className="text-sm text-gray-600">Full access to your Stripe dashboard. Manage payouts, refunds, and disputes yourself.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-purple-100 p-1.5 rounded-full mt-0.5">
                      <Building className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Your Business, Your Responsibility</p>
                      <p className="text-sm text-gray-600">You handle chargebacks and refunds directly with Stripe - not through us.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
                <button
                  onClick={() => setShowTrustModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmStripe}
                  disabled={connecting}
                  className="flex-1 px-4 py-2.5 bg-[#635BFF] hover:bg-[#5851DB] text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      Continue to Stripe
                      <ExternalLink className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Connected but onboarding incomplete - redirect to Stripe dashboard to complete
  if (status?.connected && !status?.onboardingComplete) {
    return (
      <div className="mb-6">
        {/* Card */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-6 relative">
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
          
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
            <div className="bg-amber-500 p-2 sm:p-3 rounded-xl flex-shrink-0">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                Almost There! 🎉
              </h3>
              <p className="text-sm sm:text-base text-gray-700 mb-4">
                Your Stripe account is connected but requires additional verification. 
                Please visit your Stripe Dashboard to complete the setup.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={handleOpenDashboard}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-4 sm:px-6 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
                >
                  <ExternalLink className="w-4 h-4" />
                  Complete in Stripe Dashboard
                </button>
                <button
                  onClick={() => checkStatus(true)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Status
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default StripeConnectBanner;
